<script setup>
import { computed, ref, watch, onMounted, onUnmounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { useAppStore } from '@/stores/app'
import { useEffectiveAccess } from '@/composables/useEffectiveAccess'
import { usePermissions } from '@/composables/usePermissions'
import { useTheme } from '@/composables/useTheme'
import { useCompanySwitcher } from '@/composables/useCompanySwitcher'
import { useAppUpdates } from '@/composables/useAppUpdates'
import { useNotificationsStore } from '@/stores/notifications'
import { companiesService } from '@/services/CompaniesService'
import { ROLE_LABELS } from '@/utils/permissions'
import { usePlatformFeatures } from '@/services/PlatformFeaturesService'
import { useWhatsAppUsage } from '@/composables/useWhatsAppUsage'
import { MENU_SECTIONS, CONFIGURATION_SECTIONS } from '@/config/navigation'
import {
  ChevronLeftIcon,
  SparklesIcon,
  ArrowRightOnRectangleIcon,
  PencilIcon,
  ChevronDownIcon,
  ChevronUpDownIcon,
  ChevronRightIcon,
  ShieldCheckIcon,
  CheckIcon
} from '@heroicons/vue/24/outline'

// Props para recibir la página activa y estado móvil
const props = defineProps({
  activePage: {
    type: String,
    default: ''
  },
  isMobileMenuOpen: {
    type: Boolean,
    default: false
  },
  isCollapsed: {
    type: Boolean,
    default: false
  }
})

// Emits para comunicar eventos
const emit = defineEmits(['update:activePage', 'closeMobileMenu', 'toggleCollapse'])

// Setup
const route = useRoute()
const router = useRouter()
const authStore = useAuthStore()
const appStore = useAppStore()
const notificationsStore = useNotificationsStore()
const { switchToCompany } = useCompanySwitcher()
const { isDark, toggleTheme } = useTheme()
const { latestVersion } = useAppUpdates()

const companyLogoUrl = computed(() => authStore.companyLogoUrl)

// ─── Company selector ────────────────────────────────────────────
const showCompanySelector = ref(false)
const companySelectorRef = ref(null)
const companySelectorRefMobile = ref(null)
const sidebarCompanies = ref([])

const activeCompanyName = computed(() => {
  void appStore.companyChangeKey  // reactive — recomputes on every company switch
  return companiesService.getActiveCompany()?.name || authStore.business?.name || null
})

// Role shown in the footer reflects the user's role IN the active company:
// 'owner' for companies they own, their membership role otherwise. Recomputes on switch.
const activeCompanyRole = computed(() => {
  void appStore.companyChangeKey
  return companiesService.getActiveCompany()?.role || authStore.user?.role || null
})

const activeRoleLabel = computed(() => ROLE_LABELS[activeCompanyRole.value] || ROLE_LABELS.admin)

// Plan ownership uses ONLY the active company's own role (no fallback to the
// global account role, which is always 'owner' and would mislabel external
// companies). 'Plan personal' only when you actually own the active company.
const planOwnershipLabel = computed(() => {
  void appStore.companyChangeKey
  const role = companiesService.getActiveCompany()?.role
  return role === 'owner' ? 'Plan personal' : 'Plan externo'
})

function loadSidebarCompanies() {
  const stored = companiesService.getCompaniesFromLocal()
  const activeId = companiesService.getActiveCompany()?.id
  sidebarCompanies.value = Array.isArray(stored)
    ? stored.map(c => ({ ...c, isActive: activeId ? String(c.id) === String(activeId) : !!c.isActive }))
    : []
}

function toggleCompanySelector() {
  showCompanySelector.value = !showCompanySelector.value
  if (showCompanySelector.value) loadSidebarCompanies()
}

function handleCompanySelect(company) {
  const { switched, company: applied } = switchToCompany(company)

  showCompanySelector.value = false

  if (!switched) return

  sidebarCompanies.value = sidebarCompanies.value.map((c) => ({
    ...c,
    isActive: String(c.id) === String(applied.id)
  }))
  // Al cambiar de empresa, cerrar el drawer móvil (mismo patrón que los ítems de navegación)
  closeMobileMenu()
  // Feedback al usuario lo maneja useCompanySwitcher (toast via appStore.addSuccess)
}

function handleClickOutside(event) {
  if (!showCompanySelector.value) return
  const insideDesktop = companySelectorRef.value?.contains(event.target)
  const insideMobile = companySelectorRefMobile.value?.contains(event.target)
  if (!insideDesktop && !insideMobile) {
    showCompanySelector.value = false
  }
}

watch(
  () => appStore.companyChangeKey,
  (key) => { if (key > 0) loadSidebarCompanies() }
)

onMounted(() => {
  document.addEventListener('click', handleClickOutside, true)
  loadSidebarCompanies()
})

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside, true)
})

const {
  planDisplayName,
  planType: subscriptionPlanType,
  loading: planSubscriptionLoading,
  refresh: refreshSubscriptionCurrent
} = useEffectiveAccess()

function fallbackPlanLabel(t) {
  if (t === 'free') return 'Plan Gratuito'
  if (t === 'enterprise') return 'Plan Empresarial'
  return 'Plan Profesional'
}

const planTypeLabel = computed(() => {
  if (planSubscriptionLoading.value) return ''
  if (planDisplayName.value) return planDisplayName.value
  return fallbackPlanLabel(subscriptionPlanType.value)
})

// Short plan name for the inline footer label ("Plan personal: Pro").
const PLAN_SHORT = { free: 'Free', basic: 'Básico', pro: 'Pro', annual: 'Anual', enterprise: 'Empresarial' }
const planShortLabel = computed(() => PLAN_SHORT[subscriptionPlanType.value] || '—')

async function fetchPlanType() {
  await refreshSubscriptionCurrent()
}

onMounted(() => {
  fetchPlanType()
  // Decide si la sección WhatsApp (API) es visible según el plan del titular.
  loadWhatsAppUsage()
})

watch(
  () => [authStore.token, authStore.business?.id, appStore.companyChangeKey],
  () => {
    fetchPlanType()
    // El plan puede cambiar al cambiar de empresa: recalcula el acceso a WhatsApp.
    loadWhatsAppUsage({ force: true })
  },
  { deep: true }
)

// Estado para submenús expandidos
const expandedMenus = ref(new Set())

// Resalta el ítem correcto aunque el path tenga variaciones (nombre de ruta = fuente fiable)
const menuIdByRouteName = {
  Dashboard: 'dashboard',
  DashboardCalendar: 'calendar',
  Citas: 'citas',
  DashboardClients: 'clients',
  ClientDetail: 'clients',
  DashboardServices: 'services',
  ServiceOrders: 'service-orders',
  Cobros: 'cobros',
  DashboardReports: 'reports',
  Notifications: 'notifications',
  Support: 'support',
  SupportTicketDetail: 'support',
  WhatsAppInbox: 'whatsapp',
  WhatsAppConversation: 'whatsapp'
}

// Items del menú: fuente única en src/config/navigation.ts (compartida con MobileBottomNav).
const menuItems = MENU_SECTIONS

// Ítems con `feature` solo se muestran si el toggle de plataforma está activo.
const { isFeatureEnabled } = usePlatformFeatures()
const { can } = usePermissions()

// La sección WhatsApp (bandeja + recordatorio por API) es de plan basic+. `available`
// viene de GET /whatsapp/usage (api_available: combina toggle de plataforma + plan del
// titular). En plan free queda en false y el ítem no se muestra; el enlace wa.me gratuito
// de las citas no depende de esto, así que sigue disponible.
const { available: whatsappApiAvailable, load: loadWhatsAppUsage } = useWhatsAppUsage()

/** Gate extra por ítem más allá del feature global (hoy solo WhatsApp, por plan). */
const isMenuItemAllowed = (item) => {
  if (item.id === 'whatsapp') return whatsappApiAvailable.value
  return true
}

// Filtra hijos sin permiso; con un solo hijo visible el ítem vuelve a ser enlace directo.
const visibleMenuItems = computed(() => menuItems
  .filter((item) => !item.hidden && (!item.feature || isFeatureEnabled(item.feature)) && isMenuItemAllowed(item))
  .map((item) => {
    if (!item.submenu) return item
    const submenu = item.submenu.filter((sub) => !sub.permission || can(sub.permission))
    return submenu.length > 1 ? { ...item, submenu } : { ...item, hasSubmenu: false, submenu: undefined }
  }))

// Opciones de la sección Configuration (estilo: título + línea + lista)
// Perfil se abre desde la tarjeta del usuario, no desde la lista.
const configurationItems = CONFIGURATION_SECTIONS.filter((item) => !item.hidden)

// Función para cambiar página
const navigateTo = (item) => {
  if (item.hasSubmenu) {
    // Toggle submenu expansion
    toggleSubmenu(item.id)
  } else {
    router.push(item.path)
    emit('update:activePage', item.id)
    // Cerrar automáticamente el menú móvil después de navegar
    closeMobileMenu()
  }
}

// Función para toggle de submenús
const toggleSubmenu = (menuId) => {
  if (expandedMenus.value.has(menuId)) {
    expandedMenus.value.delete(menuId)
  } else {
    expandedMenus.value.add(menuId)
  }
}

// Función para navegar a submenú
const navigateToSubmenu = (submenuItem, parentId = 'settings') => {
  // Si tiene submenú, expandirlo
  if (submenuItem.hasSubmenu) {
    if (!expandedMenus.value.has(submenuItem.id)) {
      expandedMenus.value.add(submenuItem.id)
    }
  }
  // Navegar a la página
  router.push(submenuItem.path)
  emit('update:activePage', parentId)
  closeMobileMenu()
}

// Función para manejar clic en submenú (navega Y expande si tiene submenú)
const handleSubmenuClick = (submenuItem, parentId = 'settings') => {
  // Siempre navegar a la página si tiene path
  if (submenuItem.path) {
    router.push(submenuItem.path)
    emit('update:activePage', parentId)
  }
  
  // Si tiene submenú, hacer toggle
  if (submenuItem.hasSubmenu) {
    toggleSubmenu(submenuItem.id)
  } else {
    // Solo cerrar menú móvil si no tiene submenú (navegación final)
    closeMobileMenu()
  }
}

// Toggle y navegación para ítems de la sección Configuration que tienen submenú
const handleConfigSubmenuClick = (configItem) => {
  if (configItem.hasSubmenu) {
    const expandId = 'config-' + configItem.id
    if (expandedMenus.value.has(expandId)) {
      expandedMenus.value.delete(expandId)
    } else {
      expandedMenus.value.add(expandId)
    }
    if (configItem.path) {
      router.push(configItem.path)
      emit('update:activePage', 'settings')
    }
  }
  closeMobileMenu()
}

// Función para cerrar menú móvil
const closeMobileMenu = () => {
  emit('closeMobileMenu')
}

// Función para toggle collapse
const toggleCollapse = () => {
  emit('toggleCollapse')
}

// Función para cerrar sesión
const handleLogout = async () => {
  try {
    await authStore.logout()
    appStore.clearErrors()
    router.push('/')
  } catch (error) {
    console.error('Error en logout:', error)
    appStore.clearErrors()
    router.push('/')
  }
}

// Activo según la ruta (nombre de ruta prioriza sobre path; activePage en App.vue puede quedar desactualizado)
const isActive = (item) => {
  const p = route.path
  if (item.id === 'dashboard') {
    return p === '/dashboard' || p === '/dashboard/'
  }
  const mapped = menuIdByRouteName[route.name]
  if (mapped !== undefined) {
    return item.id === mapped
  }
  return p === item.path || p.startsWith(item.path + '/')
}

// Rutas anidadas bajo el mismo ítem (p. ej. /settings/companies/list)
const isSubmenuActive = (submenuItem) => {
  if (!submenuItem.path) return false
  const p = route.path
  const base = submenuItem.path
  return p === base || (!submenuItem.exact && p.startsWith(base + '/'))
}

const pathMatchesItem = (path, base) => {
  if (!base) return false
  return path === base || path.startsWith(base + '/')
}

// Función para determinar si un submenú padre tiene hijos activos
const isSubmenuParentActive = (submenuItem) => {
  if (submenuItem.hasSubmenu && submenuItem.submenu) {
    return submenuItem.submenu.some(child => pathMatchesItem(route.path, child.path))
  }
  return false
}

// Función para verificar si el menú padre debe estar expandido por defecto
const shouldExpandByDefault = (item) => {
  if (item.hasSubmenu && item.submenu) {
    return item.submenu.some(submenu => {
      if (pathMatchesItem(route.path, submenu.path)) return true
      if (submenu.hasSubmenu && submenu.submenu) {
        return submenu.submenu.some(nested => pathMatchesItem(route.path, nested.path))
      }
      return false
    })
  }
  return false
}

// Auto-expandir menú si hay una ruta activa dentro
const initializeExpandedMenus = () => {
  visibleMenuItems.value.forEach(item => {
    if (shouldExpandByDefault(item)) {
      expandedMenus.value.add(item.id)
      // También expandir submenús anidados si es necesario
      if (item.submenu) {
        item.submenu.forEach(submenu => {
          if (submenu.hasSubmenu && submenu.submenu) {
            const hasActiveChild = submenu.submenu.some(nested => route.path === nested.path)
            if (hasActiveChild) {
              expandedMenus.value.add(submenu.id)
            }
          }
        })
      }
    }
  })
}

// Inicializar al montar
initializeExpandedMenus()
// Los permisos pueden llegar después: re-expande cuando aparece un submenú.
watch(visibleMenuItems, initializeExpandedMenus)

// Función para navegar al perfil del usuario
const navigateToProfile = () => {
  router.push('/dashboard/settings/profile')
  emit('update:activePage', 'settings')
  closeMobileMenu()
}

const navigateToCompanies = () => {
  router.push('/dashboard/settings/companies')
  emit('update:activePage', 'settings')
  closeMobileMenu()
}
</script>

<template>
  <!-- Overlay semitransparente para móvil con click para cerrar -->
  <div 
    v-if="isMobileMenuOpen" 
    class="fixed inset-0 z-40 lg:hidden touch-none transition-opacity duration-300"
    style="background-color: rgba(0, 0, 0, 0.4); backdrop-filter: blur(4px);"
    @click="closeMobileMenu"
    title="Toca para cerrar el menú"
  ></div>

  <!-- Sidebar -->
  <aside 
      :class="[
        // Ancho base y colapsado
        'bg-light-background dark:bg-dark-background shadow-sm h-screen max-h-screen flex flex-col transition-all duration-300 z-50 border-r border-light-border dark:border-dark-border',
        // Ancho responsivo - más ancho cuando está colapsado para que el avatar se vea completo
        isCollapsed ? 'lg:w-20' : 'lg:w-64',
      'w-60 sm:w-64 md:w-72 max-lg:overflow-x-hidden', // Móvil siempre tamaño completo
      // Posicionamiento
      'lg:translate-x-0', // Siempre visible en desktop
      isMobileMenuOpen ? 'translate-x-0 left-0' : '-translate-x-full', // Controlado en móvil
      'lg:fixed fixed', // Siempre fixed para que funcione como menú
      'rounded-r-2xl lg:rounded-none' // Bordes redondeados en móvil
    ]"
  >
    <!-- Header del sidebar en Desktop -->
    <div class="hidden lg:block p-4 border-b border-light-border dark:border-dark-border bg-light-background dark:bg-dark-background flex-shrink-0">
      <div class="flex items-center justify-between">
        <div 
          :class="[
            'flex items-center space-x-2 transition-all duration-300',
            isCollapsed ? 'justify-center w-full' : ''
          ]"
        >
          <button
            type="button"
            @click="navigateToCompanies"
            title="Ir a múltiples empresas"
            aria-label="Ir a múltiples empresas"
            :class="[
              'rounded-lg flex items-center justify-center transition-all duration-300 overflow-hidden hover:scale-105 focus:outline-none focus:ring-2 focus:ring-light-accent/40 dark:focus:ring-dark-accent/40',
              isCollapsed ? 'w-10 h-10' : 'w-8 h-8',
              companyLogoUrl ? 'bg-transparent' : 'bg-light-accent dark:bg-dark-accent'
            ]"
          >
            <!-- Mostrar logo de empresa si existe -->
            <img 
              v-if="companyLogoUrl"
              :src="companyLogoUrl"
              :alt="authStore.business?.name || 'Logo'"
              class="w-full h-full object-cover"
            />
            <!-- Mostrar icono genérico como fallback -->
            <SparklesIcon 
              v-else
              :class="[
                'text-light-cta-text dark:text-dark-cta-text transition-all duration-300',
                isCollapsed ? 'w-5 h-5' : 'w-4 h-4'
              ]" 
            />
          </button>
          <div 
            v-if="!isCollapsed"
            class="transition-all duration-300 min-w-0 flex-1"
          >
            <span class="font-semibold text-light-text-primary dark:text-dark-text-primary">GIOR</span><span class="accent-text">.mx</span>
            <div ref="companySelectorRef" class="relative mt-1">
              <button
                type="button"
                @click.stop="toggleCompanySelector"
                class="flex items-center gap-1 w-full text-left group/company rounded-md px-1 -mx-1 py-0.5 hover:bg-light-accent/10 dark:hover:bg-dark-accent/10 transition-colors duration-200"
              >
                <span class="text-xs text-light-text-secondary dark:text-dark-text-secondary font-medium truncate">{{ activeCompanyName || authStore.business?.name || 'Tu marca aquí' }}</span>
                <ChevronUpDownIcon class="w-3 h-3 flex-shrink-0 text-light-text-secondary dark:text-dark-text-secondary group-hover/company:text-light-accent dark:group-hover/company:text-dark-accent transition-colors" />
              </button>

              <!-- Company dropdown -->
              <Transition name="dropdown">
                <div
                  v-if="showCompanySelector && !isCollapsed"
                  class="absolute left-0 top-full mt-1 w-56 z-[60] bg-light-background dark:bg-dark-background-modal border border-light-border dark:border-dark-border rounded-xl shadow-xl overflow-hidden"
                >
                  <div class="py-1 max-h-52 overflow-y-auto">
                    <button
                      v-for="company in sidebarCompanies"
                      :key="company.id"
                      type="button"
                      @click.stop="handleCompanySelect(company)"
                      :class="[
                        'company-dropdown-item w-full text-left px-3 py-2.5 text-sm flex items-center gap-2 transition duration-150 ease-out active:scale-[0.97]',
                        company.isActive
                          ? 'bg-light-accent/10 dark:bg-dark-accent/10 text-light-accent dark:text-dark-accent font-medium'
                          : 'text-light-text-primary dark:text-dark-text-primary hover:bg-light-border/40 dark:hover:bg-dark-border/40 active:bg-light-border/60 dark:active:bg-dark-border/60'
                      ]"
                    >
                      <span class="truncate flex-1">{{ company.name }}</span>
                      <CheckIcon v-if="company.isActive" class="w-4 h-4 flex-shrink-0 text-light-accent dark:text-dark-accent" />
                    </button>
                  </div>
                  <div v-if="sidebarCompanies.length === 0" class="px-3 py-4 text-center text-xs text-light-text-secondary dark:text-dark-text-secondary">
                    No hay empresas registradas
                  </div>
                </div>
              </Transition>
            </div>
          </div>
        </div>
        
        <!-- Collapse Button -->
        <button
          v-if="!isCollapsed"
          @click="toggleCollapse"
          class="p-2 rounded-lg bg-light-border/50 dark:bg-dark-border/50 text-light-text-secondary dark:text-dark-text-secondary hover:bg-light-accent dark:hover:bg-dark-accent hover:text-light-cta-text dark:hover:text-dark-cta-text transition-all duration-300 transform hover:scale-110"
          :title="'Colapsar sidebar'"
        >
          <ChevronLeftIcon class="w-5 h-5 transition-transform duration-300" />
        </button>
      </div>
      
      <!-- Expand Button (cuando está colapsado) -->
      <div 
        v-if="isCollapsed" 
        class="flex justify-center mt-3 pt-3 border-t border-light-border dark:border-dark-border"
      >
        <button
          @click="toggleCollapse"
          class="p-2 rounded-lg bg-light-border/50 dark:bg-dark-border/50 text-light-text-secondary dark:text-dark-text-secondary hover:bg-light-accent dark:hover:bg-dark-accent hover:text-light-cta-text dark:hover:text-dark-cta-text transition-all duration-300 transform hover:scale-110"
          :title="'Expandir sidebar'"
        >
          <ChevronLeftIcon class="w-5 h-5 transition-transform duration-300 rotate-180" />
        </button>
      </div>
    </div>

    <!-- Header del sidebar en móvil -->
    <div class="lg:hidden relative p-4 border-b border-light-border dark:border-dark-border bg-light-background dark:bg-dark-background flex-shrink-0">
      <div class="flex items-center justify-between">
        <div class="flex items-center space-x-2 flex-1 min-w-0">
          <button
            type="button"
            @click="navigateToCompanies"
            title="Ir a múltiples empresas"
            aria-label="Ir a múltiples empresas"
            :class="[
              'w-8 h-8 rounded-lg flex items-center justify-center overflow-hidden transition-all duration-300 hover:scale-105 focus:outline-none focus:ring-2 focus:ring-light-accent/40 dark:focus:ring-dark-accent/40',
              companyLogoUrl ? 'bg-transparent' : 'bg-light-accent dark:bg-dark-accent'
            ]"
          >
            <img 
              v-if="companyLogoUrl"
              :src="companyLogoUrl"
              :alt="authStore.business?.name || 'Logo'"
              class="w-full h-full object-cover"
            />
            <SparklesIcon 
              v-else
              class="w-4 h-4 text-light-cta-text dark:text-dark-cta-text" 
            />
          </button>
          <div class="min-w-0 flex-1">
            <span class="font-semibold text-light-text-primary dark:text-dark-text-primary">GIOR.mx</span>
            <div ref="companySelectorRefMobile" class="mt-1">
              <button
                type="button"
                @click.stop="toggleCompanySelector"
                class="flex items-center gap-1 w-full text-left group/company rounded-md px-1 -mx-1 py-0.5 hover:bg-light-accent/10 dark:hover:bg-dark-accent/10 transition-colors duration-200"
              >
                <span class="text-xs text-light-text-secondary dark:text-dark-text-secondary font-medium truncate">{{ activeCompanyName || authStore.business?.name || 'Tu marca aquí' }}</span>
                <ChevronUpDownIcon class="w-3 h-3 flex-shrink-0 text-light-text-secondary dark:text-dark-text-secondary group-hover/company:text-light-accent dark:group-hover/company:text-dark-accent transition-colors" />
              </button>

              <!-- Company dropdown (mobile) -->
              <Transition name="dropdown">
                <div
                  v-if="showCompanySelector"
                  class="absolute inset-x-4 top-full mt-1 z-[60] bg-light-background dark:bg-dark-background-modal border border-light-border dark:border-dark-border rounded-xl shadow-xl overflow-hidden"
                >
                  <div class="py-1 max-h-52 overflow-y-auto">
                    <button
                      v-for="company in sidebarCompanies"
                      :key="company.id"
                      type="button"
                      @click.stop="handleCompanySelect(company)"
                      :class="[
                        'company-dropdown-item w-full text-left px-3 py-2.5 text-sm flex items-center gap-2 transition duration-150 ease-out active:scale-[0.97]',
                        company.isActive
                          ? 'bg-light-accent/10 dark:bg-dark-accent/10 text-light-accent dark:text-dark-accent font-medium'
                          : 'text-light-text-primary dark:text-dark-text-primary hover:bg-light-border/40 dark:hover:bg-dark-border/40 active:bg-light-border/60 dark:active:bg-dark-border/60'
                      ]"
                    >
                      <span class="truncate flex-1">{{ company.name }}</span>
                      <CheckIcon v-if="company.isActive" class="w-4 h-4 flex-shrink-0 text-light-accent dark:text-dark-accent" />
                    </button>
                  </div>
                  <div v-if="sidebarCompanies.length === 0" class="px-3 py-4 text-center text-xs text-light-text-secondary dark:text-dark-text-secondary">
                    No hay empresas registradas
                  </div>
                </div>
              </Transition>
            </div>
          </div>
        </div>
        <button 
          @click="closeMobileMenu"
          class="p-3 rounded-xl bg-light-border dark:bg-dark-border text-light-text-secondary dark:text-dark-text-secondary hover:bg-light-accent dark:hover:bg-dark-accent hover:text-light-cta-text dark:hover:text-dark-cta-text transition-all border-none duration-300 transform hover:scale-110 flex-shrink-0"
          title="Cerrar menú"
        >
          <ChevronLeftIcon class="w-5 h-5" />
        </button>
      </div>
    </div>

    <!-- overscroll-contain: sin esto, al llegar al tope de la lista el scroll se
         encadena al <main> de atrás (muy visible en iOS) -->
    <nav :class="['mt-4 flex-1 overflow-y-auto overflow-x-hidden overscroll-contain overscroll-x-none transition-all duration-300 pb-4', isCollapsed ? 'lg:px-1 px-2 sm:px-3' : 'px-2 sm:px-3']">
      <div class="space-y-1">
        <div v-for="item in visibleMenuItems" :key="item.id">
          <!-- Main Menu Button -->
          <button 
            @click="navigateTo(item)"
            :title="isCollapsed ? item.label : ''"
            class="lg:tooltip"
            :class="[
              isActive(item)
                ? 'bg-light-accent dark:bg-dark-accent text-light-cta-text dark:text-dark-cta-text shadow-md'
                : 'text-light-text-primary dark:text-dark-text-primary hover-accent-bg-20 dark:hover-accent-bg-20 hover-accent-text transform [@media(hover:hover)]:hover:scale-105 hover-accent-shadow [@media(hover:hover)]:hover:translate-x-1',
              'group flex items-center w-full text-sm font-medium rounded-lg transition-all duration-300',
              // En móvil/tablet siempre padding normal, en desktop depende del estado colapsado
              'px-3 sm:px-4 py-2',
              isCollapsed ? 'lg:px-2 lg:justify-center' : ''
            ]"
          >
            <!-- Icono -->
            <div
              :class="[
                'relative w-6 h-6 sm:w-7 sm:h-7 flex items-center justify-center rounded-md transition-all duration-300',
                // En móvil/tablet siempre margin derecho, en desktop depende del estado colapsado
                'mr-2 sm:mr-3 lg:mr-2 lg:sm:mr-3',
                isCollapsed ? 'lg:mx-auto' : '',
                isActive(item)
                  ? 'bg-light-cta-text/20 dark:bg-dark-cta-text/20'
                  : 'bg-light-border/50 dark:bg-dark-border/50 group-hover-accent-bg-20'
              ]"
            >
              <component
                :is="item.icon"
                :class="[
                  'w-4 h-4 sm:w-5 sm:h-5 transition-all duration-300',
                  isActive(item)
                    ? 'text-light-cta-text dark:text-dark-cta-text'
                    : 'text-light-text-secondary dark:text-dark-text-secondary group-hover-accent-text'
                ]"
              />
              <!-- Badge de no leídas: mismo contador que la vista de notificaciones -->
              <span
                v-if="item.id === 'notifications' && notificationsStore.unreadCount > 0"
                class="absolute -top-1 -right-1 min-w-[16px] h-4 px-1 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center leading-none"
              >{{ notificationsStore.unreadCount > 9 ? '9+' : notificationsStore.unreadCount }}</span>
            </div>
            
            <!-- Label (siempre visible en móvil/tablet, colapsable en desktop) -->
            <span 
              :class="[
                'flex-1 min-w-0 truncate text-sm font-medium tracking-wide transition-all duration-300 text-left',
                'lg:block', // Siempre visible en móvil/tablet
                isCollapsed ? 'lg:hidden' : '', // Solo se oculta en desktop cuando está colapsado
                isActive(item) 
                  ? 'text-light-cta-text dark:text-dark-cta-text' 
                  : 'text-light-text-primary dark:text-dark-text-primary group-hover-accent-text group-hover:font-semibold'
              ]"
            >
              {{ item.label }}
            </span>
            
            <!-- Indicador de submenú o activo -->
            <div 
              :class="[
                'ml-auto transition-all duration-300',
                'lg:block', // Siempre visible en móvil/tablet
                isCollapsed ? 'lg:hidden' : '' // Solo se oculta en desktop cuando está colapsado
              ]"
            >
              <!-- Indicador de submenú con rotación -->
              <ChevronRightIcon 
                v-if="item.hasSubmenu"
                :class="[
                  'w-4 h-4 transition-all duration-300 transform',
                  expandedMenus.has(item.id) ? 'rotate-90' : 'rotate-0',
                  isActive(item) 
                    ? 'text-light-cta-text dark:text-dark-cta-text' 
                    : 'text-light-text-secondary dark:text-dark-text-secondary group-hover-accent-text'
                ]"
              />
              <!-- Indicador activo -->
              <div 
                v-else-if="isActive(item)"
                class="w-2 h-2 bg-light-cta-text dark:bg-dark-cta-text rounded-full animate-pulse"
              ></div>
            </div>
          </button>

          <!-- Submenu Items - Versión normal (expandido) -->
          <div 
            v-if="item.hasSubmenu && expandedMenus.has(item.id) && !isCollapsed"
            class="ml-4 mt-1 space-y-1 transition-all duration-300 pr-1"
          >
            <div v-for="submenuItem in item.submenu" :key="submenuItem.id">
              <button
                @click="handleSubmenuClick(submenuItem, item.id)"
                :class="[
                  isSubmenuActive(submenuItem) || isSubmenuParentActive(submenuItem)
                    ? 'accent-bg-10 dark:accent-bg-10 accent-text border-l-2 accent-border'
                    : 'text-light-text-secondary dark:text-dark-text-secondary hover-accent-bg-20 dark:hover-accent-bg-20 hover-accent-text',
                  'group flex items-center w-full text-sm font-medium rounded-lg transition-all duration-300 px-3 py-2 [@media(hover:hover)]:hover:translate-x-1'
                ]"
              >
                <!-- Submenu Icon -->
                <div 
                  :class="[
                    'w-5 h-5 flex items-center justify-center rounded-md mr-2 transition-all duration-300',
                    isSubmenuActive(submenuItem) || isSubmenuParentActive(submenuItem)
                      ? 'accent-bg-20'
                      : 'bg-light-border/30 dark:bg-dark-border/30 '
                  ]"
                >
                  <component 
                    :is="submenuItem.icon"
                    :class="[
                      'w-3 h-3 transition-all duration-300 ',
                      isSubmenuActive(submenuItem) || isSubmenuParentActive(submenuItem)
                        ? 'accent-text'
                        : 'text-light-text-secondary dark:text-dark-text-secondary group-hover-accent-text'
                    ]"
                  />
                </div>
                
                <!-- Submenu Label -->
                <span 
                  :class="[
                    'flex-1 min-w-0 truncate text-xs font-medium tracking-wide transition-all duration-300 text-left',
                    isSubmenuActive(submenuItem) || isSubmenuParentActive(submenuItem)
                      ? 'accent-text font-semibold'
                      : 'text-light-text-secondary dark:text-dark-text-secondary group-hover-accent-text'
                  ]"
                >
                  {{ submenuItem.label }}
                </span>

                <!-- Nested submenu indicator -->
                <ChevronRightIcon 
                  v-if="submenuItem.hasSubmenu"
                  :class="[
                    'w-3 h-3 transition-all duration-300 transform ml-auto',
                    expandedMenus.has(submenuItem.id) ? 'rotate-90' : 'rotate-0',
                    isSubmenuActive(submenuItem) || isSubmenuParentActive(submenuItem)
                      ? 'accent-text'
                      : 'text-light-text-secondary dark:text-dark-text-secondary'
                  ]"
                />

                <!-- Active indicator for submenu -->
                <div 
                  v-if="isSubmenuActive(submenuItem) && !submenuItem.hasSubmenu"
                  class="ml-auto w-1.5 h-1.5 bg-light-accent dark:bg-dark-accent rounded-full animate-pulse"
                ></div>
              </button>

              <!-- Nested submenu items (level 3) -->
              <div 
                v-if="submenuItem.hasSubmenu && expandedMenus.has(submenuItem.id)"
                class="ml-4 mt-1 space-y-1 transition-all duration-300"
              >
                <button
                  v-for="nestedItem in submenuItem.submenu"
                  :key="nestedItem.id"
                  @click="navigateToSubmenu(nestedItem)"
                  :class="[
                    isSubmenuActive(nestedItem)
                      ? 'accent-bg-10 dark:accent-bg-10 accent-text border-l-2 accent-border'
                      : 'text-light-text-secondary dark:text-dark-text-secondary hover-accent-bg-20 dark:hover-accent-bg-20 hover-accent-text',
                    'group flex items-center w-full text-xs font-medium rounded-lg transition-all duration-300 px-3 py-1.5 [@media(hover:hover)]:hover:translate-x-1'
                  ]"
                >
                  <!-- Nested Icon -->
                  <div 
                    :class="[
                      'w-4 h-4 flex items-center justify-center rounded-md mr-2 transition-all duration-300',
                      isSubmenuActive(nestedItem)
                        ? 'accent-bg-20'
                        : 'bg-light-border/30 dark:bg-dark-border/30'
                    ]"
                  >
                    <component 
                      :is="nestedItem.icon"
                      :class="[
                        'w-2.5 h-2.5 transition-all duration-300',
                        isSubmenuActive(nestedItem)
                          ? 'accent-text'
                          : 'text-light-text-secondary dark:text-dark-text-secondary group-hover-accent-text'
                      ]"
                    />
                  </div>
                  
                  <!-- Nested Label -->
                  <span 
                    :class="[
                      'text-xs font-medium tracking-wide transition-all duration-300',
                      isSubmenuActive(nestedItem)
                        ? 'accent-text font-semibold'
                        : 'text-light-text-secondary dark:text-dark-text-secondary group-hover-accent-text'
                    ]"
                  >
                    {{ nestedItem.label }}
                  </span>

                  <!-- Active indicator -->
                  <div 
                    v-if="isSubmenuActive(nestedItem)"
                    class="ml-auto w-1.5 h-1.5 bg-light-accent dark:bg-dark-accent rounded-full animate-pulse"
                  ></div>
                </button>
              </div>
            </div>
          </div>

          <!-- Submenu Items - Versión colapsada (solo iconos) -->
          <div 
            v-if="item.hasSubmenu && isCollapsed && expandedMenus.has(item.id)"
            class="hidden lg:flex lg:flex-col lg:items-center lg:space-y-1 lg:mt-1 lg:px-1 transition-all duration-300"
          >
            <button
              v-for="submenuItem in item.submenu"
              :key="'collapsed-' + submenuItem.id"
              @click="navigateToSubmenu(submenuItem, item.id)"
              :title="submenuItem.label"
              :class="[
                isSubmenuActive(submenuItem)
                  ? 'bg-light-accent dark:bg-dark-accent text-light-cta-text dark:text-dark-cta-text shadow-md '
                  : 'text-light-text-secondary dark:text-dark-text-secondary hover-accent-bg-20 hover-accent-text',
                'group flex items-center justify-center w-8 h-8 rounded-lg transition-all duration-300 transform [@media(hover:hover)]:hover:scale-110'
              ]"
            >
              <component 
                :is="submenuItem.icon"
                :class="[
                  'w-4 h-4 transition-all duration-300',
                  isSubmenuActive(submenuItem)
                    ? 'text-light-cta-text dark:text-dark-cta-text'
                    : 'text-light-text-secondary dark:text-dark-text-secondary group-hover-accent-text'
                ]"
              />
              
              <!-- Active indicator for collapsed submenu -->
              <div 
                v-if="isSubmenuActive(submenuItem)"
                class="absolute -right-0.5 -top-0.5 w-2 h-2 bg-light-accent dark:bg-dark-accent rounded-full animate-pulse accent-bg"
              ></div>
            </button>
          </div>
        </div>

        <!-- Sección CONFIGURATION: título + línea + opciones (estilo referencia) -->
        <div v-if="!isCollapsed" class="mt-6 pt-2">
          <p class="px-3 text-xs font-semibold uppercase tracking-wider text-light-text-title-menu dark:text-dark-text-secondary">
            Configuration
          </p>
          <div class="my-2 border-t border-light-border dark:border-dark-border"></div>
          <div class="space-y-0.5 mt-1">
            <template v-for="configItem in configurationItems" :key="configItem.id">
              <button
                v-if="!configItem.hasSubmenu"
                @click="handleSubmenuClick(configItem)"
                :class="[
                  configItem.hideFrom1040 ? 'min-[1040px]:hidden' : '',
                  isSubmenuActive(configItem)
                    ? 'bg-light-accent dark:bg-dark-accent text-light-cta-text dark:text-dark-cta-text shadow-md'
                    : 'text-light-text-primary dark:text-dark-text-primary hover-accent-bg-20 dark:hover-accent-bg-20 hover-accent-text transform [@media(hover:hover)]:hover:scale-105 hover-accent-shadow [@media(hover:hover)]:hover:translate-x-1',
                  'group flex items-center w-full text-sm font-medium rounded-lg transition-all duration-300 px-3 py-2'
                ]"
              >
                <div
                  :class="[
                    'w-6 h-6 flex items-center justify-center rounded-md mr-2',
                    isSubmenuActive(configItem)
                      ? 'bg-light-cta-text/20 dark:bg-dark-cta-text/20'
                      : 'bg-light-border/50 dark:bg-dark-border/50 group-hover-accent-bg-20'
                  ]"
                >
                  <component
                    :is="configItem.icon"
                    :class="[
                      'w-4 h-4',
                      isSubmenuActive(configItem)
                        ? 'text-light-cta-text dark:text-dark-cta-text'
                        : 'text-light-text-secondary dark:text-dark-text-secondary group-hover-accent-text'
                    ]"
                  />
                </div>
                <span class="flex-1 text-left" :class="isSubmenuActive(configItem) ? '' : 'group-hover-accent-text group-hover:font-semibold'">{{ configItem.label }}</span>
              </button>
              <div v-else class="space-y-0.5">
                <button
                  @click="handleConfigSubmenuClick(configItem)"
                  :class="[
                    isSubmenuActive(configItem) || isSubmenuParentActive(configItem)
                      ? 'bg-light-accent dark:bg-dark-accent text-light-cta-text dark:text-dark-cta-text shadow-md'
                      : 'text-light-text-primary dark:text-dark-text-primary hover-accent-bg-20 dark:hover-accent-bg-20 hover-accent-text transform [@media(hover:hover)]:hover:scale-105 hover-accent-shadow [@media(hover:hover)]:hover:translate-x-1',
                    'group flex items-center w-full text-sm font-medium rounded-lg transition-all duration-300 px-3 py-2 [@media(hover:hover)]:hover:translate-x-1'
                  ]"
                >
                  <div
                    :class="[
                      'w-6 h-6 flex items-center justify-center rounded-md mr-2',
                      isSubmenuActive(configItem) || isSubmenuParentActive(configItem)
                        ? 'bg-light-cta-text/20 dark:bg-dark-cta-text/20'
                        : 'bg-light-border/50 dark:bg-dark-border/50'
                    ]"
                  >
                    <component
                      :is="configItem.icon"
                      class="w-4 h-4 text-current"
                    />
                  </div>
                  <span class="flex-1 text-left">{{ configItem.label }}</span>
                  <ChevronRightIcon
                    :class="[
                      'w-4 h-4 transition-transform',
                      expandedMenus.has('config-' + configItem.id) ? 'rotate-90' : 'rotate-0'
                    ]"
                  />
                </button>
                <div
                  v-if="expandedMenus.has('config-' + configItem.id)"
                  class="ml-4 space-y-0.5"
                >
                  <button
                    v-for="nested in configItem.submenu"
                    :key="nested.id"
                    @click="navigateToSubmenu(nested)"
                    :class="[
                      isSubmenuActive(nested)
                        ? 'accent-bg-10 accent-text border-l-2 accent-border'
                        : 'text-light-text-secondary dark:text-dark-text-secondary hover-accent-bg-20 hover-accent-text',
                      'group flex items-center w-full text-xs font-medium rounded-lg px-3 py-1.5 [@media(hover:hover)]:hover:translate-x-1'
                    ]"
                  >
                    <component :is="nested.icon" class="w-3 h-3 mr-2 accent-text opacity-80" />
                    <span>{{ nested.label }}</span>
                  </button>
                </div>
              </div>
            </template>
          </div>
        </div>

        <!-- Configuración colapsada: solo iconos -->
        <div v-if="isCollapsed" class="mt-6 pt-2 lg:flex lg:flex-col lg:items-center lg:space-y-1">
          <p class="px-2 text-[10px] font-semibold uppercase tracking-wider text-light-text-secondary dark:text-dark-text-secondary lg:mb-1">Config</p>
          <button
            v-for="configItem in configurationItems.filter(c => !c.hasSubmenu)"
            :key="'collapsed-' + configItem.id"
            @click="handleSubmenuClick(configItem)"
            :title="configItem.label"
            :class="[
              configItem.hideFrom1040 ? 'min-[1040px]:hidden' : '',
              isSubmenuActive(configItem)
                ? 'bg-light-accent dark:bg-dark-accent text-light-cta-text dark:text-dark-cta-text'
                : 'text-light-text-secondary dark:text-dark-text-secondary hover:bg-light-accent/20 dark:hover:bg-dark-accent/20',
              'flex items-center justify-center w-8 h-8 rounded-lg transition-all'
            ]"
          >
            <component :is="configItem.icon" class="w-4 h-4" />
          </button>
          <button
            v-for="configItem in configurationItems.filter(c => c.hasSubmenu)"
            :key="'collapsed-' + configItem.id"
            @click="() => { if (configItem.path) router.push(configItem.path); emit('update:activePage', 'settings'); closeMobileMenu() }"
            :title="configItem.label"
            :class="[
              isSubmenuActive(configItem) || isSubmenuParentActive(configItem)
                ? 'bg-light-accent dark:bg-dark-accent text-light-cta-text dark:text-dark-cta-text'
                : 'text-light-text-secondary dark:text-dark-text-secondary hover:bg-light-accent/20 dark:hover:bg-dark-accent/20',
              'flex items-center justify-center w-8 h-8 rounded-lg transition-all'
            ]"
          >
            <component :is="configItem.icon" class="w-4 h-4" />
          </button>
        </div>
      </div>
    </nav>

    <!-- Sección de Usuario en la parte inferior -->
    <div class="mt-auto px-3 pb-3 flex-shrink-0">
      <div class="border-t border-light-border dark:border-dark-border pt-3">

        <!-- Usuario Card -->
        <div 
          :class="[
            'user-card rounded-lg bg-light-background dark:bg-dark-background border border-light-border dark:border-dark-border p-3 mb-2 transition-all duration-300 shadow-sm',
            isCollapsed ? 'lg:border-none lg:p-1.5 lg:mx-1' : ''
          ]"
        >
          <!-- Avatar y Info -->
          <div 
            :class="[
              'user-card-header flex items-center transition-all duration-300',
              isCollapsed ? 'lg:justify-center lg:mb-2' : 'mb-2'
            ]"
          >
            <button
              type="button"
              @click="navigateToProfile"
              title="Ir al perfil"
              aria-label="Ir al perfil personal"
              :class="[
                'user-card-header-avatar rounded-full flex shrink-0 items-center justify-center transition-all duration-300 overflow-hidden min-w-0',
                'hover:scale-105 focus:outline-none focus:ring-2 focus:ring-light-accent/40 dark:focus:ring-dark-accent/40',
                isCollapsed ? 'lg:size-9 lg:max-h-9 lg:max-w-9' : 'size-8 max-h-8 max-w-8',
                authStore.avatarUrl ? 'bg-transparent' : 'bg-light-accent dark:bg-dark-accent'
              ]"
            >
              <!-- Mostrar imagen de perfil si existe -->
              <img 
                v-if="authStore.avatarUrl"
                :src="authStore.avatarUrl"
                :alt="authStore.userName"
                class="h-full w-full min-h-0 min-w-0 max-h-full max-w-full rounded-full object-cover"
              />
              <!-- Mostrar iniciales como fallback -->
              <span 
                v-else
                :class="[
                  'user-card-header-info font-semibold text-light-cta-text dark:text-dark-cta-text transition-all duration-300',
                  isCollapsed ? 'lg:text-sm' : 'text-xs'
                ]"
              >
                {{ authStore.userName.charAt(0).toUpperCase() }}
              </span>
            </button>
            
            <div 
              :class="[
                'ml-3 flex-1 min-w-0 transition-all duration-300',
                isCollapsed ? 'lg:hidden' : ''
              ]"
            >
              <div class="flex items-center justify-between">
                <button type="button" class="flex-1 min-w-0 text-left" title="Ir al perfil" @click="navigateToProfile">
                  <p class="text-sm font-medium text-light-text-primary dark:text-dark-text-primary truncate">
                    {{ authStore.userName }}
                  </p>
                  <p class="text-xs text-light-text-secondary dark:text-dark-text-secondary">
                    {{ activeRoleLabel }}
                  </p>
                </button>
                <button
                  @click="navigateToProfile"
                  title="Ir al perfil"
                  class="p-1 rounded-md text-light-text-secondary dark:text-dark-text-secondary hover-accent-bg-20 hover-accent-text transition-all duration-300"
                >
                  <PencilIcon class="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          </div>
          
          <!-- Controles: Theme Toggle, Logout y Versión -->
          <div 
            :class="[
              'user-card-footer flex flex-col transition-all duration-300',
              isCollapsed ? 'lg:flex-col lg:items-center lg:space-y-2' : ''
            ]"
          >
            <!-- Controles principales (Theme + Logout) -->
            <div 
              :class="[
                'user-card-footer-controls flex gap-2 transition-all duration-300',
                isCollapsed ? 'lg:flex-col lg:space-y-2 lg:items-center' : 'items-center space-x-3 justify-between'
              ]"
            >
              <!-- Theme Toggle -->
              <button
                @click="toggleTheme"
                :title="isDark ? 'Cambiar a modo claro' : 'Cambiar a modo oscuro'"
                class="user-card-footer-toggle relative inline-flex h-4 w-8 items-center rounded-full transition-colors duration-300 focus:outline-none focus:ring-2 focus:ring-light-accent/30 dark:focus:ring-dark-accent/30"
                :class="[
                  isDark
                    ? 'accent-bg'
                    : 'bg-light-border dark:bg-dark-border',
                  isCollapsed ? 'lg:hidden' : ''
                ]"
              >
                <span
                  class="h-2.5 w-2.5 transform rounded-full bg-light-background dark:bg-dark-background transition-transform duration-300 shadow-sm flex items-center justify-center"
                  :class="[
                    isDark ? 'translate-x-4' : 'translate-x-0.5'
                  ]"
                >
                  <!-- Icon inside the toggle -->
                  <svg 
                    v-if="!isDark"
                    class="w-1.5 h-1.5 text-light-text-secondary dark:text-dark-text-secondary"
                    fill="currentColor" 
                    viewBox="0 0 24 24"
                  >
                    <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/>
                  </svg>
                  <svg 
                    v-else
                    class="w-1.5 h-1.5 accent-text"
                    fill="currentColor" 
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    stroke-width="2"
                  >
                    <circle cx="12" cy="12" r="4" fill="currentColor"/>
                    <line x1="12" y1="2" x2="12" y2="4" stroke="currentColor" stroke-width="2"/>
                    <line x1="12" y1="20" x2="12" y2="22" stroke="currentColor" stroke-width="2"/>
                    <line x1="4.22" y1="4.22" x2="5.64" y2="5.64" stroke="currentColor" stroke-width="2"/>
                    <line x1="18.36" y1="18.36" x2="19.78" y2="19.78" stroke="currentColor" stroke-width="2"/>
                    <line x1="2" y1="12" x2="4" y2="12" stroke="currentColor" stroke-width="2"/>
                    <line x1="20" y1="12" x2="22" y2="12" stroke="currentColor" stroke-width="2"/>
                    <line x1="4.22" y1="19.78" x2="5.64" y2="18.36" stroke="currentColor" stroke-width="2"/>
                    <line x1="18.36" y1="5.64" x2="19.78" y2="4.22" stroke="currentColor" stroke-width="2"/>
                  </svg>
                </span>
              </button>

              <!-- Cerrar sesión: label e icono son hermanos dentro de un flex; colapsado (lg) solo icono -->
              <button
                type="button"
                @click="handleLogout"
                title="Cerrar sesión"
                aria-label="Cerrar sesión"
                :class="[
                  'user-card-footer-logout w-full min-w-0 rounded-md transition-colors duration-300 focus:outline-none focus:ring-2 focus:ring-light-accent/30 dark:focus:ring-dark-accent/30',
                  'text-light-text-secondary dark:text-dark-text-secondary hover:bg-light-border/30 dark:hover:bg-dark-border/30 hover:text-light-text-primary dark:hover:text-dark-text-primary',
                  isCollapsed ? 'lg:px-1.5 lg:py-1.5' : 'px-2.5 py-1.5'
                ]"
              >
                <div
                  :class="[
                    'flex  w-full min-w-0 items-center gap-2',
                    isCollapsed ? 'lg:justify-center' : 'justify-end'
                  ]"
                >
                  <span
                    class="text-xs font-medium whitespace-nowrap text-left"
                    :class="isCollapsed ? 'lg:hidden' : ''"
                  >Cerrar sesión</span>
                  <ArrowRightOnRectangleIcon
                    :class="[
                      'flex-shrink-0 text-red-600 dark:text-red-400',
                      isCollapsed ? 'w-5 h-5 lg:w-5 lg:h-5' : 'w-3.5 h-3.5'
                    ]"
                  />
                </div>
              </button>
            </div>
            
            <!-- Plan, versión y enlaces legales -->
            <div
              :class="[
                'user-card-footer-version text-xs text-light-text-secondary dark:text-dark-text-secondary transition-all duration-300',
                isCollapsed ? 'lg:hidden' : ''
              ]"
            >
              <!-- Plan con contexto de propiedad, en una sola línea -->
              <p
                class="mt-2 rounded-lg bg-light-border/30 dark:bg-dark-border/30 px-2.5 py-1.5 truncate min-w-0"
                :title="`${planOwnershipLabel} : ${planSubscriptionLoading ? '…' : planShortLabel}`"
              >
                <span class="font-medium text-light-text-secondary dark:text-dark-text-secondary">{{ planOwnershipLabel }}:&nbsp;</span><span class="font-semibold text-light-text-primary dark:text-dark-text-primary">{{ planSubscriptionLoading ? '…' : planShortLabel }}</span>
              </p>
              <!-- Marca · versión · enlaces legales -->
              <div class="flex items-center justify-between gap-2 mt-2 opacity-70">
                <span class="font-medium whitespace-nowrap">GIOR · v{{ latestVersion }}</span>
                <div class="flex flex-col items-end gap-0.5 shrink-0 lg:flex-row lg:items-center lg:gap-1.5">
                  <router-link
                    to="/terminos-y-condiciones"
                    target="_blank"
                    class="hover:opacity-100 hover-accent-text transition-all duration-200"
                  >Términos</router-link>
                  <span class="hidden lg:inline opacity-40">·</span>
                  <router-link
                    to="/privacidad"
                    target="_blank"
                    class="hover:opacity-100 hover-accent-text transition-all duration-200"
                  >Privacidad</router-link>
                </div>
              </div>
            </div>
          </div>
        </div>

      </div>
    </div>
  </aside>
</template>

<style scoped>

/* Estados de hover mejorados - usa variable CSS.
   Solo en dispositivos con hover real (desktop); en touch el :hover
   se queda pegado tras tocar y el box-shadow se ve al abrir/cerrar el drawer. */
@media (hover: hover) {
  nav button:hover {
    transform: translateX(4px) scale(1.02);
    box-shadow: 0 4px 12px color-mix(in srgb, var(--light-accent) 30%, transparent);
  }

  :global(.dark) nav button:hover {
    box-shadow: 0 4px 12px color-mix(in srgb, var(--dark-accent) 30%, transparent);
  }
}

/* Animaciones suaves */
.transition-all {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

/* Eliminar bordes azules por defecto - usa variable CSS.
   :focus-visible en vez de :focus: el anillo aparece solo en navegación
   por teclado (a11y intacta) y no queda pegado al tocar/clickear, que era
   lo que se veía como "sombreado" por fila al abrir/cerrar el drawer. */
button:focus-visible {
  outline: none;
  box-shadow: 0 0 0 2px color-mix(in srgb, var(--light-accent) 50%, transparent);
  border-radius: 8px;
}

:global(.dark) button:focus-visible {
  box-shadow: 0 0 0 2px color-mix(in srgb, var(--dark-accent) 50%, transparent);
}

/* Dropdown de empresas: el item activo ya se distingue por bg/texto/check,
   no necesita el anillo de focus accent (queda muy cargado al hacer clic). */
.company-dropdown-item:focus,
.company-dropdown-item:focus-visible {
  outline: none;
  box-shadow: none;
}

button {
  border: none;
  outline: none;
}

/* Overlay con blur */
.backdrop-blur-sm {
  backdrop-filter: blur(4px);
}

/* Bordes redondeados en móvil */
@media (max-width: 1024px) {
  aside {
    border-top-right-radius: 1rem;
    border-bottom-right-radius: 1rem;
  }
}

/* Ajustes para pantallas muy pequeñas */
@media (max-width: 480px) {
  aside {
    max-width: 85vw;
    box-shadow: 0 10px 25px rgba(0, 0, 0, 0.15);
  }
  
  nav button {
    padding: 0.625rem 0.5rem;
    font-size: 0.8rem;
  }
  
  .w-7 {
    width: 1.5rem;
    height: 1.5rem;
  }
}

/* Indicador activo animado */
.animate-pulse {
  animation: pulse-dot 2s infinite;
}

@keyframes pulse-dot {
  0%, 100% {
    opacity: 1;
    transform: scale(1);
  }
  50% {
    opacity: 0.8;
    transform: scale(1.1);
  }
}

/* Shadow improvements */
.shadow-lg {
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
}

/* Button hover improvements */
nav button:hover {
  transition: all 0.2s ease;
}

/* Dropdown transitions */
.dropdown-enter-active,
.dropdown-leave-active {
  transition: opacity 0.15s ease, transform 0.15s ease;
}

.dropdown-enter-from,
.dropdown-leave-to {
  opacity: 0;
  transform: translateY(-4px) scale(0.98);
}

/* Modal transitions */
.modal-enter-active,
.modal-leave-active {
  transition: opacity 0.25s ease;
}

.modal-enter-from,
.modal-leave-to {
  opacity: 0;
}

.modal-enter-active > div,
.modal-leave-active > div {
  transition: transform 0.25s ease;
}

.modal-enter-from > div,
.modal-leave-to > div {
  transform: scale(0.95) translateY(10px);
}
</style>
